import {
    getPledgePayOpenBalanceAvailable,
    getPledgePayOpenCreditPoolForGateway,
} from './pledge-pay-open-credit-context';
import { mgrPledgeBillGatewayCreditApplyInputFromUseCheckbox } from './pledge-pay-open-payment-gateway';
import {
    mgrPledgeModalRecalcOpenApplyCap,
    pledgePayBillUpdateSummary,
} from './pledge-pay-balance';
import {
    formatCurrency,
    getPaymentMode,
    hasPendingMatanah,
    isPledgePayRecurringDurationReady,
    pledgePayUiRoot,
    shouldFilterStatementPledges,
} from './pledge-pay-utils';
import { getPledgeRowAmountToPay, getPledgeRowBalanceDue } from './pay-pledge-map';
import { getKioskConfig } from '../shared/kiosk-config';

import * as FieldValidation from '../shared/field-validation.js';
import { setPledgePayDetailsExpanded } from './pledge-pay-details';

function sumPledgeAmountPaidInputs($container) {
    const $wrap = $container.find('#set_pledge_payment_table');
    const $inputs = ($wrap.length && $wrap.find('.mgr-pledge-gateway-payment-table').length)
        ? $wrap.find('.mgr-pledge-gateway-payment-table input[name="amount_paid"]')
        : $container.find('input[name="amount_paid"]');
    const $filtered = $inputs.filter(function () {
        const $tr = $(this).closest('tr');
        if (!$tr.length) {
            return true;
        }
        const $cb = $tr.find('input.statement-pledge-select');
        return !$cb.length || $cb.prop('checked');
    });
    return $filtered.toArray().reduce(
        (sum, i) => sum + (parseFloat(String(i.value).replace(/,/g, '')) || 0),
        0
    );
}

function sumPledgeBalanceDueOriginal($modal) {
    const $rows = $modal.find('#set_pledge_payment_table .mgr-pledge-gateway-payment-table tbody tr').length
        ? $modal.find('#set_pledge_payment_table .mgr-pledge-gateway-payment-table tbody tr')
        : $modal.find('#set_payment_table tbody tr');
    const $filtered = $rows.filter(function () {
        const $cb = $(this).find('input.statement-pledge-select');
        return !$cb.length || $cb.prop('checked');
    });
    return $filtered.toArray().reduce((sum, tr) => {
        return sum + getPledgeRowBalanceDue($(tr));
    }, 0);
}

function sumPledgeAmountPaidForGateway($root, gatewayId) {
    const gid = String(gatewayId);
    let sum = 0;
    $root.find(`tr[data-gateway-id="${gid}"]`).each(function () {
        if (shouldSkipAmountToPayRowForStatement($(this), $root)) {
            return;
        }
        const $inp = $(this).find('input[name="amount_paid"]');
        if ($inp.length && !$inp.prop('disabled')) {
            sum += parseFloat(String($inp.val()).replace(/,/g, '')) || 0;
        }
    });
    return sum;
}

function syncPledgePayGatewayTotals($root) {
    $root.find('.mgr-pledge-gateway-group').each(function () {
        const $section = $(this);
        const total = sumPledgeAmountPaidForGateway($root, $section.attr('data-gateway-id'));
        const pendingAmount = $section.find('.matanah-save-btn').length > 0;
        const amount = '$' + formatCurrency(total);
        $section.find('.mgr-pledge-gateway-total-value').text(
            pendingAmount ? (total > 0 ? `${amount} + amount needed` : 'Amount needed') : amount
        );
    });
}

function getAmountToPayTableBodyRows($root) {
    const $wrap = $root.find('#set_pledge_payment_table');
    if ($wrap.find('.mgr-pledge-gateway-payment-table').length) {
        return $wrap.find('.mgr-pledge-gateway-payment-table tbody tr');
    }
    const $table = $root.find('#set_pledge_payment_table table:first, #set_payment_table').first();
    if (!$table.length) {
        return $();
    }
    return $table.find('tbody tr');
}

function shouldSkipAmountToPayRowForStatement($row, $root) {
    const paymentMode = getPaymentMode($root);
    if (!shouldFilterStatementPledges($root, paymentMode)) {
        return false;
    }
    const $cb = $row.find('input.statement-pledge-select');
    return $cb.length > 0 && !$cb.is(':checked');
}

function isPledgeAmountToPayRowInvalid($row, $root) {
    if (shouldSkipAmountToPayRowForStatement($row, $root)) {
        return false;
    }
    const $inp = $row.find('input[name="amount_paid"]');
    if ($inp.length && $inp.prop('disabled')) {
        return false;
    }
    const rawValue = $inp.length ? $inp.val() : getPledgeRowAmountToPay($row);
    const raw = String(rawValue ?? '').replace(/,/g, '').trim();
    const num = parseFloat(raw);
    return raw === '' || !Number.isFinite(num) || num <= 0;
}

function pledgeAmountToPayRowsInvalid($root) {
    if (getPaymentMode($root) === 'recurring') {
        return false;
    }
    let invalid = false;
    getAmountToPayTableBodyRows($root).each(function () {
        if (isPledgeAmountToPayRowInvalid($(this), $root)) {
            invalid = true;
            return false;
        }
    });
    return invalid;
}

function syncPledgeAmountToPayInputStyles($root) {
    if (getPaymentMode($root) === 'recurring') {
        return;
    }
    getAmountToPayTableBodyRows($root).each(function () {
        const $row = $(this);
        if (shouldSkipAmountToPayRowForStatement($row, $root)) {
            $row.find('input[name="amount_paid"]').css('border-color', '');
            return;
        }
        const $inp = $row.find('input[name="amount_paid"]');
        if (!$inp.length || $inp.prop('disabled')) {
            return;
        }
        const invalid = isPledgeAmountToPayRowInvalid($row, $root);
        $inp.css('border-color', invalid ? 'red' : '');
        if (invalid) {
            setPledgePayDetailsExpanded($row.closest('.mgr-pledge-gateway-group')[0], true);
        }
    });
}

function getOpenCreditApplyExceedReason(applyRaw, groupPay, pool) {
    if (!Number.isFinite(applyRaw)) {
        return 'Enter a valid account credit amount.';
    }
    if (applyRaw > groupPay + 0.005) {
        return 'Account credit cannot exceed the amount to pay. Reduce this amount to continue.';
    }
    if (applyRaw > pool + 0.005) {
        return 'Account credit cannot exceed the available credit. Reduce this amount to continue.';
    }
    return '';
}

function setOpenCreditApplyError($inp, message) {
    if (!$inp || !$inp.length) {
        return;
    }
    const invalid = message !== '';
    $inp.css('border-color', invalid ? 'red' : '');
    const $err = $inp
        .closest('.mgr-gateway-credit-panel, #mgr_open_payment_amount_wrap, .mgr-open-credit-apply-muted')
        .find('.mgr-gateway-credit-apply-error, .mgr-open-credit-apply-error')
        .first();
    if (!$err.length) {
        return;
    }
    if (invalid) {
        $err.text(message).prop('hidden', false);
    } else {
        $err.text('').prop('hidden', true);
    }
}

function pledgeOpenApplyAmountExceedsPayingToday($root) {
    const payType = $root.find('#payment_type').val();
    const mode = $root.find('input[name="payment_mode"]:checked').val();
    if (payType !== 'pay' || mode === 'recurring') {
        return false;
    }
    const tableTotal = sumPledgeAmountPaidInputs($root);
    let anyInvalid = false;

    if ($root.find('.mgr-pledge-gateway-stack').length) {
        $root.find('.mgr-gateway-credit-apply').each(function () {
            setOpenCreditApplyError($(this), '');
        });
        $root.find('.mgr-gateway-use-credit:checked').each(function () {
            const $cb = $(this);
            const gid = String($cb.data('gateway-id') || '');
            const $inp = mgrPledgeBillGatewayCreditApplyInputFromUseCheckbox($cb);
            if (!$inp.length || $inp.prop('disabled')) {
                return;
            }
            const raw = String($inp.val() ?? '').replace(/,/g, '').trim();
            if (raw === '') {
                setOpenCreditApplyError($inp, '');
                return;
            }
            const applyRaw = parseFloat(raw);
            const pool = getPledgePayOpenCreditPoolForGateway(gid);
            const groupPay = sumPledgeAmountPaidForGateway($root, gid);
            const reason = getOpenCreditApplyExceedReason(applyRaw, groupPay, pool);
            setOpenCreditApplyError($inp, reason);
            if (reason) {
                anyInvalid = true;
            }
        });
        return anyInvalid;
    }

    const openBalAvailable = getPledgePayOpenBalanceAvailable();
    const hasInteractiveOpen =
        openBalAvailable > 0 && $root.find('#mgr_open_payment_active_state').is(':visible');
    const $inp = $root.find('#mgr_open_payment_apply_amount');
    if (!hasInteractiveOpen || !$root.find('#mgr_use_open_payment_balance').prop('checked')) {
        setOpenCreditApplyError($inp, '');
        return false;
    }
    if (!$inp.length || $inp.prop('disabled')) {
        setOpenCreditApplyError($inp, '');
        return false;
    }
    const raw = String($inp.val() ?? '').replace(/,/g, '').trim();
    if (raw === '') {
        setOpenCreditApplyError($inp, '');
        return false;
    }
    const applyRaw = parseFloat(raw);
    const reason = getOpenCreditApplyExceedReason(applyRaw, tableTotal, openBalAvailable);
    setOpenCreditApplyError($inp, reason);
    return reason !== '';
}

function updatePledgePayNowButtonEnabledState($anyDescendant) {
    const $root = pledgePayUiRoot($anyDescendant);
    const $btn = $root.find('#pay_now_button');
    const $openApply = $root.find('#mgr_open_payment_apply_amount');
    if (!$btn.length) {
        return;
    }
    if (hasPendingMatanah($root)) {
        $btn.prop('disabled', true);
        setOpenCreditApplyError($openApply, '');
        $root.find('.mgr-gateway-credit-apply').each(function () {
            setOpenCreditApplyError($(this), '');
        });
        return;
    }
    if (getPaymentMode($root) === 'recurring') {
        $btn.prop('disabled', !!$btn.data('statementSelectDisabled') || !isPledgePayRecurringDurationReady($root));
        setOpenCreditApplyError($openApply, '');
        $root.find('.mgr-gateway-credit-apply').each(function () {
            setOpenCreditApplyError($(this), '');
        });
        return;
    }
    syncPledgeAmountToPayInputStyles($root);
    const amountInvalid = pledgeAmountToPayRowsInvalid($root);
    $btn.data('pledgeAmountInvalid', amountInvalid);
    const statementBlocked = !!$btn.data('statementSelectDisabled');
    const openApplyExceedsPayingToday = pledgeOpenApplyAmountExceedsPayingToday($root);
    $btn.prop('disabled', statementBlocked || amountInvalid || openApplyExceedsPayingToday);
}

function syncPledgeAmountToPayValidation($anyDescendant) {
    updatePledgePayNowButtonEnabledState($anyDescendant);
}

function updateStatementPledgeSelectionValidation($container) {
    if (!$container || !$container.length) {
        return;
    }

    const $fullPayment = $container.find('#full_payment');
    const $payNowButton = $container.find('#pay_now_button');

    if (!$payNowButton.length) {
        return;
    }

    const $checkboxes = $container.find('input.statement-pledge-select');
    const $enabledSelections = $checkboxes.filter(function () {
        return !$(this).prop('disabled')
            && !$(this).closest('.pledge-payment-td-select').hasClass('d-none');
    });

    const requiresSelection = $fullPayment.length
        && $fullPayment.is(':checked')
        && $enabledSelections.length > 0;

    if (!requiresSelection) {
        $payNowButton.removeData('statementSelectDisabled');
        updatePledgePayNowButtonEnabledState($container);
        return;
    }

    const anyChecked = $enabledSelections.filter(':checked').length > 0;

    if (anyChecked) {
        $payNowButton.removeData('statementSelectDisabled');
    } else {
        $payNowButton.data('statementSelectDisabled', true);
    }
    updatePledgePayNowButtonEnabledState($container);
}

function runChangeAmount(el) {
    const $container = $(el).closest('.modal, #pledge_pay_body, .modal-content');
    const $row = $(el).closest('tr');

    let val = parseFloat(el.value.replace(/,/g, '')) || 0;
    const min = parseFloat(el.min || 0);
    const max = parseFloat(el.max);

    const $msgBox = $container.find('.pledge-payment-message-div');
    const $msgUl = $container.find('.pledge-payment-message-ul');

    $msgBox.hide();
    $(el).css('border-color', '');

    if (el.value !== '' && val < min) {
        val = min;
        el.value = min;
        $(el).css('border-color', 'red');
        $msgUl.html(
            `<li><i class="fa-regular fa-circle-x"></i>&nbsp;
             The amount entered was less than the minimum. Please enter $${formatCurrency(min)} or higher.</li>`
        );
        $msgBox.show();
    }

    if (el.value !== '' && val > max) {
        val = max;
        el.value = max;
        $(el).css('border-color', 'red');
        $msgUl.html(
            `<li><i class="fa-regular fa-circle-x"></i>&nbsp;
             Please enter an amount not more than the amount due.</li>`
        );
        $msgBox.show();
    }

    const total = sumPledgeAmountPaidInputs($container);
    $container.find('#amt_due_total, #amt_due_total_payment').html(`$${formatCurrency(total)}`);

    const $mgrModal = $('#setPledgePaymentModal');
    if ($container.closest('#setPledgePaymentModal').length && $mgrModal.length) {
        mgrPledgeModalRecalcOpenApplyCap($mgrModal);
    }
    const $pledgeBody = $container.closest('#pledge_pay_body');
    if ($pledgeBody.length && $pledgeBody.find('#mgr_open_payment_apply_amount').length) {
        mgrPledgeModalRecalcOpenApplyCap($pledgeBody);
    }

    syncPledgeAmountToPayValidation($container);

    pledgePayBillUpdateSummary(pledgePayUiRoot($container));
}

$(document).on('input change', '#set_payment_table input[name="amount_paid"], .mgr-pledge-gateway-payment-table input[name="amount_paid"]', function () {
    if ($(this).prop('disabled')) {
        return;
    }
    runChangeAmount(this);
});

$(document).on('keydown', '#set_payment_table input[name="amount_paid"], .mgr-pledge-gateway-payment-table input[name="amount_paid"], .matanah_amt_entry', function (e) {
    if (e.key === 'Enter') {
        e.preventDefault();
        return false;
    }
});

$(document).on('change', '#pledge_pay_body #pledge_payment_duration, #setPledgePaymentModal #pledge_payment_duration', function () {
    updatePledgePayNowButtonEnabledState($(this));
});

$(document).on('change', '#full_payment', function () {
    const $container = $(this).closest('#pledge_pay_body, .modal-content, .modal');
    updateStatementPledgeSelectionValidation($container);
});

$(document).on('change', 'input.statement-pledge-select', function () {
    const $container = $(this).closest('#pledge_pay_body, .modal-content, .modal');
    updateStatementPledgeSelectionValidation($container);
    const $payRoot = pledgePayUiRoot($container);
    if ($payRoot.length && $payRoot.find('#mgr_open_payment_apply_amount').length) {
        mgrPledgeModalRecalcOpenApplyCap($payRoot);
    }
    pledgePayBillUpdateSummary($payRoot.length ? $payRoot : $container);
});

FieldValidation.bindDelegatedCustomValidity(
    document,
    'input[name="matanah_amount"]',
    function () {
        return `Please Enter Amount in the following format: 9999.99, the minimum you can enter is $${getKioskConfig().minTransAmt}`;
    },
    '.matanahAmount'
);

export {
    pledgeAmountToPayRowsInvalid,
    shouldSkipAmountToPayRowForStatement,
    syncPledgeAmountToPayInputStyles,
    updatePledgePayNowButtonEnabledState,
    updateStatementPledgeSelectionValidation,
    sumPledgeAmountPaidInputs,
    sumPledgeBalanceDueOriginal,
    sumPledgeAmountPaidForGateway,
    syncPledgePayGatewayTotals,
};
